import { lazy } from "react";

// Sentinel survives a single page reload so we never get stuck in a reload
// loop. Cleared by App.js once the new build successfully boots.
const RELOAD_KEY = "oy_chunk_reloaded_v1";

const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const msg = String(error.message || "");
  return (
    /Loading chunk [^\s]+ failed/.test(msg) ||
    /Loading CSS chunk [^\s]+ failed/.test(msg) ||
    /Failed to fetch dynamically imported module/.test(msg) ||
    /error loading dynamically imported module/.test(msg)
  );
};

// Wraps React.lazy with a one-shot reload on chunk-load failure. Typical
// trigger: a deploy invalidated the hashed chunk the running tab references,
// so the dynamic import 404s. A single full reload picks up the new
// index.html (and therefore the new chunk hashes) and the user lands on the
// page they meant to open. If reload-once didn't fix it, we let the error
// bubble to RouteErrorBoundary, which renders a Reload UI.
export function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch((error) => {
      if (!isChunkLoadError(error)) throw error;
      const alreadyReloaded = window.sessionStorage.getItem(RELOAD_KEY);
      if (!alreadyReloaded) {
        try {
          window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
          /* private mode / quota — proceed anyway */
        }
        // Suspense stays on the fallback while the reload navigates away.
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    })
  );
}

export const CHUNK_RELOAD_KEY = RELOAD_KEY;
